import { Link } from '@tanstack/react-router';
import { Baby, CheckSquare, Inbox, Sparkles, Users } from 'lucide-react';
import { useDashboard, useFamily } from '../../api/queries';
import { Avatar, Card, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { formatDay, timeAgo } from '../../lib/format';
import { TaskList } from '../tasks/TaskCard';

export function DashboardPage() {
  const dash = useDashboard();
  const family = useFamily().data;
  if (!dash.data) return <Spinner />;
  const d = dash.data;
  return (
    <>
      <PageHeader title={family?.name ?? 'Home'} subtitle={formatDay(d.today)} />
      {family?.deletionScheduledFor ? (
        <Card className="mb-4 border-red-300 bg-red-50 text-sm text-red-900 dark:bg-red-900/20 dark:text-red-200">
          This household is scheduled for deletion. <Link to="/settings" className="underline">Review in Family Settings</Link>.
        </Card>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat to="/pool" icon={Inbox} label="House Pool" value={d.counts.pool} />
        <Stat to="/mine" icon={CheckSquare} label="My tasks" value={d.counts.mine} />
        {d.counts.adults.map((a) => (
          <Stat key={a.memberId} to="/partner" icon={Users} label={a.displayName} value={a.count} />
        ))}
        <Stat to="/decisions" icon={Sparkles} label="Needs your decision" value={d.pendingDecisionCount} highlight={d.pendingDecisionCount > 0} />
      </div>

      {d.counts.children.length ? (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Children</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {d.counts.children.map((c) => (
              <Link key={c.memberId} to="/children/$childId" params={{ childId: c.memberId }}>
                <Card className="flex items-center gap-3 hover:bg-[var(--surface-2)]">
                  <Avatar name={c.displayName} size={36} />
                  <div className="flex-1">
                    <p className="font-medium">{c.displayName}</p>
                    <p className="text-xs text-[var(--muted)]">{c.count} chore{c.count === 1 ? '' : 's'}</p>
                  </div>
                  <div className="text-right text-xs text-[var(--muted)]">
                    <p className="text-base font-semibold text-[var(--text)]">⭐ {c.pointsBalance}</p>
                    +{c.pointsThisWeek} this week
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ) : (
        <Card className="mt-6 flex items-center gap-3 text-sm">
          <Baby size={18} className="text-[var(--muted)]" /> <span className="flex-1">Add your children to give them chores.</span>
          <Link to="/children" className="text-[var(--accent)] hover:underline">Add a child</Link>
        </Card>
      )}

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Today</h2>
        <TaskList tasks={d.todayTasks} empty={<EmptyState title="Nothing due today for you or the pool" />} />
      </section>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Recent activity</h2>
        <Card className="divide-y divide-[var(--border)] p-0">
          {d.recentActivity.length ? (
            d.recentActivity.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="flex-1">{a.text}</span>
                <span className="shrink-0 text-xs text-[var(--muted)]">{timeAgo(a.occurredAt)}</span>
              </div>
            ))
          ) : (
            <p className="p-4 text-sm text-[var(--muted)]">No activity yet.</p>
          )}
        </Card>
      </section>
    </>
  );
}

function Stat({ to, icon: Icon, label, value, highlight }: { to: string; icon: typeof Inbox; label: string; value: number; highlight?: boolean }) {
  return (
    <Link to={to}>
      <Card className={`hover:bg-[var(--surface-2)] ${highlight ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : ''}`}>
        <Icon size={18} className="text-[var(--muted)]" />
        <p className="mt-2 text-2xl font-semibold">{value}</p>
        <p className="truncate text-xs text-[var(--muted)]">{label}</p>
      </Card>
    </Link>
  );
}
